import React, { useState, useRef, useEffect, useCallback } from "react";

export default function SelfieCaptureStep({ setCurrentStep, selectedOffer }) {
  const [capturedImage, setCapturedImage] = useState(null);
  const [hasPermissionError, setHasPermissionError] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null); // Keeps track of the active camera stream

  // Stop the camera (Crucial for turning off the green camera light on laptops!)
  // This is now much more aggressive to prevent the hardware from staying on
  const stopCamera = useCallback(() => {
    // 1. Kill tracks attached to the ref
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        track.stop();
        track.enabled = false;
      });
      streamRef.current = null;
    }
    
    // 2. Kill tracks directly attached to the video DOM element (Failsafe)
    if (videoRef.current) {
      const currentStream = videoRef.current.srcObject;
      if (currentStream) {
        currentStream.getTracks().forEach((track) => {
          track.stop();
        });
      }
      videoRef.current.srcObject = null;
    }
  }, []);

  // Initialize camera on mount, clean up on unmount
  useEffect(() => {
    let isMounted = true; // Tracks if the user is still on this step

    const initCamera = async () => {
      setHasPermissionError(false);
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: "user", // Forces front camera on smartphones
            width: { ideal: 720 },
            height: { ideal: 720 }
          }
        });
        
        // RACE CONDITION FIX: If the user clicked "Upload" or "Back" 
        // while the camera was still loading, kill it instantly!
        if (!isMounted) {
          stream.getTracks().forEach(track => track.stop());
          return; 
        }
        
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          // Note: play() is not strictly needed here if the video tag has autoPlay, 
          // but it's good practice for older browsers.
          videoRef.current.play().catch(e => console.log("Play failed:", e)); 
        }
      } catch (err) {
        console.error("Error accessing camera:", err);
        if (isMounted) setHasPermissionError(true);
      }
    };

    // Start the camera
    initCamera();

    // Cleanup: When the component unmounts (Upload clicked or Back button)
    return () => {
      isMounted = false; // Flag that we've left the page
      stopCamera();      // Turn off the hardware
    };
  }, [stopCamera]);

  // Restart camera specifically for retaking photos
  const startCameraManual = useCallback(async () => {
    setHasPermissionError(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "user",
          width: { ideal: 720 },
          height: { ideal: 720 }
        }
      });
      
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error("Error accessing camera:", err);
      setHasPermissionError(true);
    }
  }, []);

  // Capture the photo
  const handleCapture = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      
      // Set canvas dimensions to match video to maintain aspect ratio
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      const ctx = canvas.getContext("2d");
      
      // Mirror the canvas context so the saved photo matches the mirrored video feed
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
      
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      // Convert canvas to a Base64 image string
      const imageUrl = canvas.toDataURL("image/jpeg", 0.9);
      setCapturedImage(imageUrl);
      
      // Turn off camera since we have the photo
      stopCamera();
    }
  };

  // Retake photo
  const handleRetake = () => {
    setCapturedImage(null);
    startCameraManual();
  };

  // Upload photo
  const handleUpload = () => {
    console.log("Uploading Selfie Data URL...", capturedImage);
    // Because this triggers a state change in LandingPage.js that unmounts this component,
    // the useEffect cleanup return () => stopCamera() will fire automatically!
    setCurrentStep(14); 
  };

  const lenderName = selectedOffer?.lender_name || "CredFin";

  return (
    <div className="w-full max-w-[400px] mx-auto bg-white min-h-[100dvh] font-atkinson flex flex-col relative pb-[130px]">
      
      {/* Hidden canvas used to process the image */}
      <canvas ref={canvasRef} className="hidden" />

      {/* Header & Progress Bar */}
      <div className="px-5 pt-6 pb-4 border-b border-transparent">
        <div className="flex justify-center mb-4">
          <div className="text-center leading-none">
            <h1 className="text-[#205A6D] font-bold text-[22px] tracking-tight">
              {lenderName}
            </h1>
            <p className="text-[#7ea4b1] text-[9px] font-semibold tracking-widest mt-1 uppercase">Loans made Easy</p>
          </div>
        </div>
        
        <div className="flex items-center justify-between">
          <button onClick={() => setCurrentStep(12)} className="w-6 h-6 flex items-center justify-start text-gray-700">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
          </button>
          
          <div className="flex-1 mx-4 h-[4px] bg-[#e6f4ed] rounded-full overflow-hidden">
            <div className="h-full bg-[#12f280] w-[84%] rounded-full"></div>
          </div>
          <span className="text-[12px] text-gray-500 font-medium">84%</span>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-5 pt-2 flex-1 flex flex-col items-center">
        <div className="w-full text-left mb-8">
          <h2 className="text-[22px] font-bold text-[#1b1b1b]">Quick identity check</h2>
          <p className="text-[13px] text-[#8b8b8b] mt-2 leading-relaxed pr-6">
            Please take a quick selfie to verify you're the applicant. Under 10 seconds.
          </p>
        </div>

        {/* Camera / Preview Viewport */}
        <div className="relative w-[280px] h-[280px] flex items-center justify-center mb-8">
          {/* Dashed green outer circle */}
          <div className="absolute inset-0 border-[1.5px] border-dashed border-[#b3e5cc] rounded-full"></div>
          
          {/* Inner rounded square masking container */}
          <div className="relative w-[220px] h-[220px] bg-[#F7FBF9] rounded-[24px] overflow-hidden">
            
            {/* Display Error, Video Feed, or Captured Image */}
            {hasPermissionError ? (
              <div className="w-full h-full flex flex-col items-center justify-center text-center p-4">
                <p className="text-red-500 text-sm font-bold mb-2">Camera Access Denied</p>
                <p className="text-xs text-gray-500">Please allow camera permissions in your browser settings to continue.</p>
              </div>
            ) : capturedImage ? (
              <img 
                src={capturedImage} 
                alt="Captured Selfie" 
                className="w-full h-full object-cover"
              />
            ) : (
              <>
                <video 
                  ref={videoRef} 
                  autoPlay 
                  playsInline 
                  muted 
                  className="w-full h-full object-cover transform -scale-x-100" // -scale-x-100 mirrors the video
                />
                
                {/* Face outline icon overlaid on the live video */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="#00C676" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.8">
                    <path d="M3 7V5a2 2 0 0 1 2-2h2"></path>
                    <path d="M17 3h2a2 2 0 0 1 2 2v2"></path>
                    <path d="M21 17v2a2 2 0 0 1-2 2h-2"></path>
                    <path d="M7 21H5a2 2 0 0 1-2-2v-2"></path>
                    <rect x="9" y="8" width="1" height="3"></rect>
                    <rect x="14" y="8" width="1" height="3"></rect>
                    <path d="M9 14s1.5 2 3 2 3-2 3-2"></path>
                  </svg>
                </div>
              </>
            )}

            {/* Orange Corner Brackets */}
            <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-[#FF7A00] rounded-tl-[24px] pointer-events-none"></div>
            <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-[#FF7A00] rounded-tr-[24px] pointer-events-none"></div>
            <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-[#FF7A00] rounded-bl-[24px] pointer-events-none"></div>
            <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-[#FF7A00] rounded-br-[24px] pointer-events-none"></div>
          </div>
        </div>

        {/* Conditional UI: Show Instructions OR Retake/Upload Buttons */}
        <div className="w-full mt-4">
          {!capturedImage ? (
            /* Instructions Card (State 1) */
            <div className="border-[2px] border-[#3B82F6] rounded-[16px] p-5 w-full bg-white shadow-sm">
              <div className="flex flex-col gap-4">
                
                <div className="flex items-center gap-3">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="5"></circle>
                    <line x1="12" y1="1" x2="12" y2="3"></line>
                    <line x1="12" y1="21" x2="12" y2="23"></line>
                    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
                    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
                    <line x1="1" y1="12" x2="3" y2="12"></line>
                    <line x1="21" y1="12" x2="23" y2="12"></line>
                    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
                    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
                  </svg>
                  <span className="text-[13px] text-[#1b1b1b] font-medium">Good lighting on your face</span>
                </div>
                
                <hr className="border-[#f0f0f0]" />
                
                <div className="flex items-center gap-3">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="6" cy="15" r="4"></circle>
                    <circle cx="18" cy="15" r="4"></circle>
                    <path d="M14 15a2 2 0 0 0-4 0"></path>
                    <path d="M2.5 13L5 7c.7-1.5 1.5-2 3-2h8c1.5 0 2.3.5 3 2l2.5 6"></path>
                  </svg>
                  <span className="text-[13px] text-[#1b1b1b] font-medium">Remove sunglasses or masks</span>
                </div>
                
                <hr className="border-[#f0f0f0]" />
                
                <div className="flex items-center gap-3">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
                    <circle cx="12" cy="13" r="4"></circle>
                  </svg>
                  <span className="text-[13px] text-[#1b1b1b] font-medium">Look straight at the camera</span>
                </div>
                
              </div>
            </div>
          ) : (
            /* Action Buttons (State 2) */
            <div className="w-full flex flex-col gap-3">
              <button 
                onClick={handleRetake}
                className="w-full bg-white text-[#1b1b1b] border border-[#e6e6e6] font-bold text-[16px] rounded-[14px] py-[16px] active:scale-[0.98] transition-transform"
              >
                Retake Selfie
              </button>
              <button 
                onClick={handleUpload}
                className="w-full bg-black text-white font-bold text-[16px] rounded-[14px] py-[16px] active:scale-[0.98] transition-transform"
              >
                Upload Selfie
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Sticky Bottom Button (Only shown in State 1) */}
      {!capturedImage && (
        <div className="fixed bottom-0 left-0 right-0 z-50 mx-auto w-full max-w-[400px] bg-white px-5 py-4 border-t border-transparent">
          <button 
            onClick={handleCapture}
            disabled={hasPermissionError}
            className="w-full bg-black text-white font-bold text-[16px] rounded-[14px] py-[18px] active:scale-[0.98] transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Capture Selfie
          </button>
        </div>
      )}
    </div>
  );
}